import React, { useState } from "react";
import {
  Table,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import tableStyles from "../../../src/assets/scss/common/table.module.scss";
import skeletonStyles from "../../../src/assets/scss/common/tableSkeleton.module.scss";
import { getLeftOffset } from "../../helpers/constant";
import { getPaginationRange } from "../../helpers/pagination";
import { DragHandle, SortableTableRow } from "./DragAndDropWrapper";
import canDragVisit from "./CanDragVisit";
import ActionGuard from "./ActionGuard";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../common/constants/PermissionConstants";
import { usePermission } from "../../context/PermissionContext";
import CommonButton from "./CommonButton";

/* ---------- Types ---------- */

export type AnyObject = Record<string, unknown>;

export interface Column<T = AnyObject> {
  key: string;
  label: string | React.ReactNode;
  type?: "link";
  textCenter?: boolean;

  sticky?: boolean;
  divider?: boolean;
  maxLength?: number;
  tooltip?: boolean;

  render?: (
    value: any,
    row?: T,
    index?: number,
    allRows?: T[],
  ) => React.ReactNode;

  onClick?: (row: any) => void;
  cell?: (row: any) => React.ReactNode;
  width?: string;
}

interface TableActions {
  onVerify?: (row: any) => void;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onRename?: (row: any) => void;
  onDelete?: (row: any) => void;
  onActivate?: (row: any) => void;
  onDownload?: (row: any) => void;
  onRights?: (row: any) => void;
  onAddValue?: (row: any) => void;
  onActualCount?: (row: any) => void;
  onEditActualCount?: (row: any) => void;
  onAssignTraining?: (row: any) => void;
  onAssignRole?: (row: any) => void;
  onAssignCategory?: (row: any) => void;
  onUpdateCategory?: (row: any) => void;
  onMove?: (row: any) => void;
  onRetrieve?: (row: any) => void;
}

interface CommonTableProps {
  columns?: Column[];
  data?: any[];
  actions?: TableActions | ((row: any) => TableActions);
  showStatus?: boolean;
  emptyText?: string;
  className?: string;
  hideAction?: boolean;
  dragAndDrop?: boolean;
  paginationShow?: boolean;
  totalCount?: number;
  scrollable?: boolean;

  skeletonReload?: string | number;
  visitScroll?: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  currentPage?: number;
  pageSize?: number;
  loading?: boolean;
  totalRecords?: number;
  onPageChange?: (page: number) => void;
  moduleName?: string;
}

/* ---------- Component ---------- */

const CommonTableIndexWise: React.FC<CommonTableProps> = ({
  columns = [],
  data = [],
  actions = {},
  emptyText = "No records found",
  pageSize = 10,
  className = "",
  hideAction = false,
  paginationShow = true,
  visitScroll = false,
  dragAndDrop = false,
  maxHeight,
  stickyHeader = false,
  totalRecords = 10,
  currentPage = 1,
  loading = false,
  onPageChange = () => { },
  moduleName,
}) => {
  const [openDropdown, setOpenDropdown] = useState<Record<number, boolean>>({});
  const { hasPermission } = usePermission();

  /* ---------- Skeleton Reload ---------- */

  const toggleDropdown = (index: number) =>
    setOpenDropdown((prev) => ({ ...prev, [index]: !prev[index] }));

  const totalPages = Math.ceil(totalRecords / pageSize);

  /* ---------- Skeleton Rows ---------- */

  const skeletonRows = Array(pageSize)
    .fill(0)
    .map((_, i) => (
      <tr key={`skeleton-${i}`}>
        {columns.map((_, ci) => (
          <td key={ci}>
            <div className={skeletonStyles.skeletonLine} />
          </td>
        ))}

        {dragAndDrop && (
          <td className="text-center">
            <div className={skeletonStyles.skeletonBadge} />
          </td>
        )}
      </tr>
    ));

  //------------------ helper ---------------------------

  return (
    <>
    <div
      className={`${tableStyles.commonTableContainer} ${visitScroll ? tableStyles.visitScrollContainer : ""
        } my-2`}
      style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}
    >
      <div className={visitScroll ? tableStyles.visitScrollWrapper : ""}>
        <Table
          className={`${tableStyles.table} ${stickyHeader ? tableStyles.stickyHeaderTable : ""} align-middle table-nowrap mb-0 ${className}`}
        >
          <thead className="table-light">
            <tr>
              {dragAndDrop && <th style={{ width: 40 }}></th>}
              {columns.map((col, colIndex) => {
                const left = col.sticky
                  ? getLeftOffset(columns, colIndex)
                  : undefined;

                return (
                  <th
                    key={col.key}
                    style={{
                      width: col.width,
                      minWidth: col.sticky ? col.width : undefined,
                      maxWidth: col.sticky ? col.width : undefined,
                      left: col.sticky ? `${left}px` : undefined,
                      zIndex: col.sticky
                        ? 20 + (columns.length - colIndex)
                        : maxHeight ? 3 : undefined,
                    }}
                    className={`
                      ${col.textCenter ? "text-center" : ""}
                      ${col.sticky ? tableStyles.stickyCol : ""}
                      ${col.divider ? tableStyles.stickyDivider : ""}
                    `}
                  >
                    {col.sticky ? (
                      <div
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          maxWidth: col.width,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </div>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              skeletonRows
            ) : data.length > 0 ? (
              data.map((row, index) => {
                const draggable = canDragVisit();
                return dragAndDrop ? (
                  <SortableTableRow
                    key={row.id}
                    row={row}
                    className={`${tableStyles.table_row_hover} ${openDropdown[index] ? tableStyles.dropdown_open : ""
                      }`}
                  >
                    {({ listeners }) => (
                      <>
                        <td className="text-center" style={{ width: 40 }}>
                          <DragHandle
                            listeners={listeners}
                            disabled={!draggable}
                            reason="Visit order is protocol-defined"
                          />
                        </td>

                        {renderCells({
                          row,
                          index,
                          columns,
                          actions,
                          hideAction,
                          openDropdown,
                          toggleDropdown,
                          data,
                          moduleName,
                          hasPermission,
                        })}
                      </>
                    )}
                  </SortableTableRow>
                ) : (
                  <tr
                    key={row.id || index}
                    className={`${tableStyles.table_row_hover} ${openDropdown[index] ? tableStyles.dropdown_open : ""
                      }`}
                  >
                    {renderCells({
                      row,
                      index,
                      columns,
                      actions,
                      hideAction,
                      openDropdown,
                      toggleDropdown,
                      data,
                      moduleName,
                      hasPermission,
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    1 + // Action column
                    columns.length
                  }
                  className="text-center text-muted"
                >
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </Table>

      
      </div>
    </div>

      {!loading && totalPages > 1 && paginationShow && (
          <div className="d-flex justify-content-between align-items-center mt-3 px-2">
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
            </small>

            <Pagination className="mb-0">
              <PaginationItem disabled={currentPage === 1}>
                <PaginationLink
                  previous
                  onClick={() => onPageChange(currentPage - 1)}
                />
              </PaginationItem>

              {getPaginationRange(currentPage, totalPages).map((item, key) => (
                <PaginationItem key={key} active={currentPage === item} disabled={item === "..."}>
                  <PaginationLink
                    onClick={item === "..." ? undefined : () => onPageChange(Number(item))}
                    style={item === "..." ? { cursor: 'default' } : undefined}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem disabled={currentPage === totalPages}>
                <PaginationLink
                  next
                  onClick={() => onPageChange(currentPage + 1)}
                />
              </PaginationItem>
            </Pagination>
          </div>
        )}

        </>
  );
};

export default CommonTableIndexWise;

const truncateWithTooltip = (value: any, max = 40) => {
  if (!value || typeof value !== "string") return value;

  const isLong = value.length > max;
  const display = isLong ? value.slice(0, max) + "..." : value;

  return <span title={isLong ? value : ""}>{display}</span>;
};

const renderCells = ({
  row,
  index,
  columns,
  actions,
  hideAction,
  openDropdown,
  toggleDropdown,
  data,
  moduleName,
  hasPermission,
}: any) =>
  columns.map((col: any, colIndex: any) => {
    const left = col.sticky ? getLeftOffset(columns, colIndex) : undefined;
    const resolvedActions =
      typeof actions === "function" ? actions(row) : actions;
    return colIndex === 0 && !hideAction && !col.cell ? (
      <td
        key={col.key}
        className={`align-middle
          ${col.textCenter ? "text-center" : ""}
          ${col.sticky ? tableStyles.stickyCol : ""}
          ${col.divider ? tableStyles.stickyDivider : ""}
        `}
        style={{
          width: col.width || "auto",
          minWidth: col.sticky ? col.width : undefined,
          maxWidth: col.sticky ? col.width : undefined,
          left: col.sticky ? `${left}px` : undefined,
          zIndex: col.sticky ? 20 + (columns.length - colIndex) : undefined,
        }}
      >
        <div
          style={{
            position: "relative",
            ...(col.sticky ? { width: col.width, minWidth: col.width, maxWidth: col.width } : {}),
          }}
        >
          {col.type === "link" &&
            (!moduleName || hasPermission(moduleName, PERMISSION_ACTIONS.VIEW)) ? (
            <a
              href="#"
              className={tableStyles.studyList}
              onClick={(e) => {
                e.preventDefault();
                col.onClick?.(row);
              }}
            >
              {col.render
                ? col.render(row[col.key], row, index, data)
                : col.tooltip
                  ? truncateWithTooltip(row[col.key], col.maxLength)
                  : row[col.key]}
            </a>
          ) : (
                    <span
            style={{
              display: "block",
              paddingRight: "35px", // reserve space for action icon
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {col.render
              ? col.render(row[col.key], row, index, data)
              : col.tooltip
                ? truncateWithTooltip(row[col.key], col.maxLength)
                : row[col.key]}
          </span>
          )}

          {(() => {
            if (Object.keys(resolvedActions).length === 0) return null;
            // Check if at least one action is permitted
            const isAnyVisible =
              !moduleName ||
              (resolvedActions.onView &&
                hasPermission(moduleName, PERMISSION_ACTIONS.VIEW)) ||
              (resolvedActions.onEdit &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT)) ||
              (resolvedActions.onAssignRole &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT)) ||
              (resolvedActions.onRename &&
                hasPermission(moduleName, PERMISSION_ACTIONS.RENAME)) ||
              (resolvedActions.onRights &&
                hasPermission(moduleName, PERMISSION_ACTIONS.ASSIGN_RIGHTS)) ||
              (resolvedActions.onAssignTraining &&
                hasPermission(moduleName, PERMISSION_ACTIONS.ADD)) ||
              (resolvedActions.onDownload &&
                hasPermission(moduleName, PERMISSION_ACTIONS.DOWNLOAD)) ||
              (resolvedActions.onAddValue &&
                hasPermission(moduleName, PERMISSION_ACTIONS.ADD_VALUES)) ||
              (resolvedActions.onDelete &&
                hasPermission(moduleName, PERMISSION_ACTIONS.DEACTIVATE)) ||
              (resolvedActions.onVerify &&
                hasPermission(moduleName, PERMISSION_ACTIONS.VERIFY)) ||
              (resolvedActions.onActualCount &&
                hasPermission(moduleName, PERMISSION_ACTIONS.ADD_ACTUAL_COUNT)) ||
              (resolvedActions.onEditActualCount &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT_ACTUAL_COUNT))||
              (resolvedActions.onAssignRole &&
                hasPermission(moduleName, PERMISSION_ACTIONS.ASSIGN_ROLE)) ||
              (resolvedActions.onAssignSubjects &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT))||
              (resolvedActions.onAssignCategory &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT)) ||
              (resolvedActions.onUpdateCategory &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT)) ||
              (resolvedActions.onMove &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT)) ||
              (resolvedActions.onRetrieve &&
                hasPermission(moduleName, PERMISSION_ACTIONS.EDIT));

            if (!isAnyVisible) return null;

            return (
              <Dropdown
                isOpen={!!openDropdown[index]}
                toggle={() => toggleDropdown(index)}
                className={tableStyles.action_container}
                direction="down"
              >
                <DropdownToggle tag="a" className={tableStyles.action_dropdown}>
                  <i className="ri-more-fill fs-18"></i>
                </DropdownToggle>

                <DropdownMenu className="dropdown-menu-end" container="body" style={{ zIndex: 1060 }}>
                  {resolvedActions.onVerify && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.VERIFY}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onVerify(row);
                        }}
                      >
                        <i className="ri-checkbox-circle-line me-2" /> Verify
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onActualCount && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.ADD_ACTUAL_COUNT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onActualCount(row);
                        }}
                      >
                        <i className="ri-add-circle-line me-2" />
                        Add Actual Count
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onEditActualCount && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT_ACTUAL_COUNT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onEditActualCount(row);
                        }}
                      >
                        <i className="ri-edit-line me-2" />
                        Edit Actual Count
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onView && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.VIEW}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onView(row);
                        }}
                      >
                        <i className="ri-eye-line me-2" /> View
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onEdit &&

                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onEdit(row);
                        }}
                      >
                        <i className="ri-edit-line me-2" /> Edit
                      </a>
                    </ActionGuard>
                  }
                  {resolvedActions.onAssignCategory && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onAssignCategory(row);
                        }}
                      >
                        <i className="ri-user-add-line me-2" /> Assign Category
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onUpdateCategory && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onUpdateCategory(row);
                        }}
                      >
                        <i className="ri-edit-line me-2" /> Update Category
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onAssignSubjects && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onAssignSubjects(row);
                        }}
                      >
                        <i className="ri-user-add-line me-2" /> Assign Subjects
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onAssignRole && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.ASSIGN_ROLE}
                    >
                       <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onAssignRole(row);
                        }}
                      >
                        <i className="ri-user-settings-line me-2" />  Assign role
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onRename && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.RENAME}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onRename(row);
                        }}
                      >
                        <i className="ri-edit-line me-2" /> Rename
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onRights &&
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.ASSIGN_RIGHTS}
                    >
                      <a
                        className="dropdown-item "
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onRights(row);
                        }}
                      >
                        <i className="ri-add-circle-line me-2" /> Assign Rights
                      </a>
                    </ActionGuard>
                  }
                  {resolvedActions.onAssignTraining &&
                    (
                      <ActionGuard
                        module={moduleName}
                        action={PERMISSION_ACTIONS.ADD}
                      >
                        <a
                          className="dropdown-item "
                          onClick={() => {
                            toggleDropdown(index);
                            resolvedActions.onAssignTraining(row);
                          }}
                        >
                          <i className="ri-add-circle-line me-2" /> Assign
                          Training
                        </a>
                      </ActionGuard>
                    )}
                  {resolvedActions.onDownload && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.DOWNLOAD}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onDownload(row);
                        }}
                      >
                        <i className="ri-download-line me-2"></i> Download
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onAddValue && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.ADD_VALUES}
                    >
                      <a
                        className="dropdown-item "
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onAddValue(row);
                        }}
                      >
                        <i className="ri-add-circle-line me-2" /> Add Values
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onMove && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT}
                    >
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onMove(row);
                        }}
                      >
                        <i className="ri-drag-move-line me-2" /> Move
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onRetrieve && (
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.EDIT}
                    >
                      <a
                        className="dropdown-item text-warning"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onRetrieve(row);
                        }}
                      >
                        <i className="ri-inbox-unarchive-line me-2" /> Retrieve
                      </a>
                    </ActionGuard>
                  )}
                  {resolvedActions.onDelete &&
                    <ActionGuard
                      module={moduleName}
                      action={PERMISSION_ACTIONS.DEACTIVATE}
                    >
                      <a
                        className="dropdown-item text-danger"
                        onClick={() => {
                          toggleDropdown(index);
                          resolvedActions.onDelete(row);
                        }}
                      >
                        <i className="ri-close-circle-line me-2" /> Deactive
                      </a>
                    </ActionGuard>
                  }
                </DropdownMenu>
              </Dropdown>
            );
          })()}
        </div>
      </td>
    ) : (
      <td
        key={col.key}
        style={{
          width: col.width || "auto",
          minWidth: col.sticky ? col.width : undefined,
          maxWidth: col.sticky ? col.width : undefined,
          left: col.sticky ? `${left}px` : undefined,
          zIndex: col.sticky ? 20 + (columns.length - colIndex) : undefined,
        }}
        className={`align-middle
          ${col.textCenter ? "text-center" : ""}
          ${col.sticky ? tableStyles.stickyCol : ""}
          ${col.divider ? tableStyles.stickyDivider : ""}
        `}
      >
        <div style={col.sticky ? { width: col.width, minWidth: col.width, maxWidth: col.width } : undefined}>
          {col.type === "link" &&
            (!moduleName || hasPermission(moduleName, PERMISSION_ACTIONS.VIEW)) ? (
            <a
              href="#"
              className={tableStyles.studyList}
              onClick={(e) => {
                e.preventDefault();
                col.onClick?.(row);
              }}
            >
              {col.render
                ? col.render(row[col.key], row)
                : col.tooltip
                  ? truncateWithTooltip(row[col.key], col.maxLength)
                  : row[col.key]}
            </a>
          ) : col.cell ? (
            col.cell(row)
          ) : col.render ? (
            col.render(row[col.key], row, index, data)
          ) : col.tooltip ? (
            (truncateWithTooltip(row[col.key], col.maxLength) ?? "-")
          ) : (
            (row[col.key] ?? "-")
          )}
        </div>
      </td>
    );
  });
